import React from "react"
import {Paper, Box, Typography, Divider} from "@mui/material"

import {OfferTierCard} from "./OfferTierCard"
import {OfferStatusChip} from "./OfferStatusChip"
import {RemoveOfferButton} from "./RemoveOfferButton"
import {offerStatusColors} from "./offerStatusColors"
import {formatOfferDate} from "./formatOfferDate"

import {CustomerOffer, RevocationReason} from "@indebted/api"
import {getOfferStatus} from "@indebted/api/customerOffersAPI"

type OfferCardProps = {
	offer: CustomerOffer
	revocationReasons: RevocationReason[]
}

function formatTimestamp(timestamp: string) {
	if (!timestamp) return ""

	return new Date(timestamp).toLocaleDateString()
}

function OfferCard({offer, revocationReasons}: OfferCardProps) {
	const status = getOfferStatus(offer)

	const borderColor = offerStatusColors(status)

	const sortedTiers = React.useMemo(
		() =>
			[...offer.Tiers].sort((a, b) => {
				if (a.TierType === "ONE_OFF" && b.TierType !== "ONE_OFF") return -1
				if (a.TierType !== "ONE_OFF" && b.TierType === "ONE_OFF") return 1
				return (a.MinimumNumberOfPayments ?? 0) - (b.MinimumNumberOfPayments ?? 0)
			}),
		[offer.Tiers],
	)

	return (
		<Paper
			variant="outlined"
			style={{
				padding: "16px",
				borderLeft: `4px solid ${borderColor}`,
			}}
		>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
				<Box>
					<Box display="flex" alignItems="flex-start" style={{gap: "8px"}} mb={1}>
						<Typography variant="h6">
							Offer available to customer from {formatOfferDate(offer.StartDate)} to{" "}
							{formatOfferDate(offer.EndDate)}
						</Typography>
						<OfferStatusChip offer={offer} />
					</Box>
					<Typography variant="body2" color="textSecondary">
						Offer ID: {offer.OfferID}
					</Typography>
				</Box>
				<RemoveOfferButton offer={offer} revocationReasons={revocationReasons} />
			</Box>

			{offer.CampaignID && (
				<Box mb={2}>
					<Typography variant="body2" color="textSecondary">
						Campaign ID: {offer.CampaignID}
					</Typography>
				</Box>
			)}

			{offer.Notes && (
				<Box mb={2}>
					<Typography variant="body2" color="textSecondary">
						Notes: {offer.Notes}
					</Typography>
				</Box>
			)}

			<Divider style={{margin: "16px 0"}} />

			<Typography variant="subtitle2" gutterBottom>
				Options
			</Typography>
			<Box display="flex" flexDirection="column" style={{gap: "12px"}}>
				{sortedTiers.map((tier, index) => (
					<OfferTierCard key={tier.TierID} tier={tier} index={index} />
				))}
			</Box>

			<Box mt={2}>
				<Typography variant="caption" color="textSecondary">
					Created: {formatTimestamp(offer.CreatedAt)}
					{offer.AgentEmail && ` by ${offer.AgentEmail}`}
				</Typography>
			</Box>
		</Paper>
	)
}

export {OfferCard}
